import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import api from '../../api';
import Swal from 'sweetalert2';
import { 
  FaPlus, 
  FaTrash, 
  FaSearch, 
  FaEdit, 
  FaEye, 
  FaTruck, 
  FaUser, 
  FaPhone, 
  FaWarehouse, 
  FaFileAlt, 
  FaCheck, 
  FaTimes, 
  FaBan,
  FaPrint,
  FaFilter,
  FaSync,
  FaBoxOpen,
  FaClipboardList,
  FaShippingFast,
  FaTimesCircle,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';

const PackingSlip = () => {
  const { user } = useContext(AuthContext);
  
  // Main data states
  const [packingSlips, setPackingSlips] = useState([]);
  const [availableDeliveryOrders, setAvailableDeliveryOrders] = useState([]);
  const [godowns, setGodowns] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeliveryOrderModal, setShowDeliveryOrderModal] = useState(false);
  const [selectedPackingSlip, setSelectedPackingSlip] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Advanced filters for delivery orders (filtered by current employee)
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const formatDate = (date) => date.toISOString().split('T')[0]; // YYYY-MM-DD

    return {
      recipientName: '',
      doNumber: '',
      dateFrom: formatDate(firstDayOfMonth),
      dateTo: formatDate(lastDayOfMonth),
      godownId: ''
    };
  });
  
  // Form states
  const [formData, setFormData] = useState({
    deliveryOrderId: '',
    vehicleNumber: '',
    contactPersonName: '',
    contactPersonPhone: '',
    remarks: '',
    godownId: '', // Fallback godown
    items: []
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryOrderSearch, setDeliveryOrderSearch] = useState('');
  const [doPage, setDoPage] = useState(1);
  const [doTotalPages, setDoTotalPages] = useState(1);
  const [doTotalCount, setDoTotalCount] = useState(0);
  const [doLoading, setDoLoading] = useState(false);
  const [doGodownMode, setDoGodownMode] = useState('none');
  const [doGodown, setDoGodown] = useState(null);

  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setIsDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Helper function to get dark mode classes
  const getDarkModeClasses = () => ({
    bg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    text: isDarkMode ? 'text-gray-100' : 'text-gray-900',
    textSecondary: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    border: isDarkMode ? 'border-gray-600' : 'border-gray-300',
    input: isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900',
    card: isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    hover: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    tableHeader: isDarkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableDivide: isDarkMode ? 'divide-gray-700' : 'divide-gray-200',
    modalBg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    modalOverlay: isDarkMode ? 'bg-black bg-opacity-60' : 'bg-gray-600 bg-opacity-50',
    label: isDarkMode ? 'text-gray-300' : 'text-gray-700',
  });

  const dm = getDarkModeClasses();

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchPackingSlips();
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    loadGodowns();
  }, []);

  // Fetch packing slips
  const fetchPackingSlips = async () => {
    setLoading(true);
    try {
      const response = await api.get('/packing-slips', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          companyId: user.user.company._id,
          ...(searchTerm && { search: searchTerm }),
          ...(statusFilter && { status: statusFilter })
        }
      });
      const data = response.data;

      if (data.success) {
        setPackingSlips(data.data);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      } else {
        console.error('Failed to fetch packing slips:', data.error);
      }
    } catch (error) {
      console.error('Error fetching packing slips:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load available delivery orders for packing slip creation (Filtered by Employee)
  const loadAvailableDeliveryOrders = async (page = 1) => {
    setDoLoading(true);
    try {
      const params = {
        companyId: user.user.company._id,
        page,
        limit: 10,
        search: deliveryOrderSearch,
        recipientName: advancedFilters.recipientName,
        doNumber: advancedFilters.doNumber,
        dateFrom: advancedFilters.dateFrom,
        dateTo: advancedFilters.dateTo,
        godownId: advancedFilters.godownId,
        employeeId: user.user._id // MANDATORY: only show employee's own orders
      };

      const response = await api.get('/packing-slips/available-delivery-orders', { params });
      const data = response.data;

      if (data.success) {
        setAvailableDeliveryOrders(data.data);
        setDoPage(data.pagination.currentPage);
        setDoTotalPages(data.pagination.totalPages);
        setDoTotalCount(data.pagination.totalCount);
      }
    } catch (error) {
      console.error('Error loading delivery orders:', error);
    } finally {
      setDoLoading(false);
    }
  };

  // Load godowns
  const loadGodowns = async () => {
    try {
      const response = await api.get('/packing-slips/godowns', {
        params: { companyId: user.user.company._id }
      });
      const data = response.data;

      if (data.success) {
        setGodowns(data.data);
      }
    } catch (error) {
      console.error('Error loading godowns:', error);
    }
  };

  // Initialize form for creation
  const initializeCreateForm = () => {
    setFormData({
      deliveryOrderId: '',
      vehicleNumber: '',
      contactPersonName: '',
      contactPersonPhone: '',
      remarks: '',
      godownId: '',
      items: []
    });
    setFormErrors({});
    setSelectedDeliveryOrder(null);
    setAvailableItems([]);
    setDeliveryOrderSearch('');
    setAdvancedFilters({
      recipientName: '',
      doNumber: '',
      dateFrom: '',
      dateTo: '',
      godownId: ''
    });
    loadAvailableDeliveryOrders(1);
    setShowDeliveryOrderModal(true);
  };

  // Handle delivery order selection
  const handleDeliveryOrderSelect = async (deliveryOrder) => {
    setSelectedDeliveryOrder(deliveryOrder);
    setFormData(prev => ({ ...prev, deliveryOrderId: deliveryOrder._id }));
    setShowDeliveryOrderModal(false);

    // Load available items for this delivery order
    try {
      const response = await api.get(`/packing-slips/delivery-order/${deliveryOrder._id}/available-items`, {
        params: { companyId: user.user.company._id }
      });
      const data = response.data;

      if (data.success) {
        const godownMode = data.data.godownMode || 'none';
        const godown = data.data.godown || null;
        setDoGodownMode(godownMode);
        setDoGodown(godown);
        setAvailableItems(data.data.items);

        // Auto-fill godownId from DO if it has a godown
        const autoGodownId = (godownMode !== 'none' && godown) ? (godown._id || godown) : '';

        // Initialize ALL items (available ones editable, used ones greyed out)
        const initialItems = data.data.items.map(item => ({
          itemId: item.itemId,
          Name: item.Name,
          quantity: 0,
          maxQuantity: item.availableQuantity,
          totalQuantity: item.totalQuantity,
          usedQuantity: item.usedQuantity,
          units: item.units,
          packingNotes: '',
          isAvailable: item.isAvailable,
          godownId: autoGodownId // Default to DO godown if exists
        }));
        setFormData(prev => ({ ...prev, items: initialItems, godownId: autoGodownId }));
        setShowCreateModal(true);
      }
    } catch (error) {
      console.error('Error loading available items:', error);
    }
  };

  // Update item quantity with validation
  const updateItemQuantity = (itemIndex, quantity) => {
    const numQuantity = parseInt(quantity) || 0;
    const maxQuantity = formData.items[itemIndex].maxQuantity;

    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => 
        idx === itemIndex 
          ? { 
              ...item, 
              quantity: Math.min(Math.max(0, numQuantity), maxQuantity)
            }
          : item
      )
    }));
  };

  // Update item godown
  const updateItemGodown = (itemIndex, gid) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => 
        idx === itemIndex ? { ...item, godownId: gid } : item
      )
    }));
  };

  // Update item packing notes
  const updateItemPackingNotes = (itemIndex, notes) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => 
        idx === itemIndex 
          ? { ...item, packingNotes: notes }
          : item
      )
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.deliveryOrderId) errors.deliveryOrderId = 'Delivery order is required';
    if (!formData.vehicleNumber.trim()) errors.vehicleNumber = 'Vehicle number is required';
    if (!formData.contactPersonName.trim()) errors.contactPersonName = 'Contact person name is required';
    if (!formData.contactPersonPhone.trim()) {
      errors.contactPersonPhone = 'Contact person phone is required';
    } else if (!/^[0-9]{10}$/.test(formData.contactPersonPhone.trim())) {
      errors.contactPersonPhone = 'Please enter a valid 10-digit phone number';
    }

    // Validate items
    const itemsWithQuantity = formData.items.filter(item => item.quantity > 0);
    if (itemsWithQuantity.length === 0) {
      errors.items = 'At least one item with quantity > 0 is required';
    } else {
      // Check if each item with quantity has a godown
      const missingGodown = itemsWithQuantity.find(i => !i.godownId && doGodownMode === 'none');
      if (missingGodown) {
        errors.items = `Godown is required for item ${missingGodown.Name}`;
      }
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      // Filter items with quantity > 0 and strip UI-only fields
      const itemsToSubmit = formData.items
        .filter(item => item.quantity > 0 && item.isAvailable)
        .map(({ maxQuantity, totalQuantity, usedQuantity, isAvailable, ...item }) => item);

      const payload = {
        ...formData,
        items: itemsToSubmit,
        createdBy: user.user._id
      };

      const response = await api.post('/packing-slips', payload);
      const data = response.data;

      if (data.success) {
        setShowCreateModal(false);
        fetchPackingSlips();
        
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: data.allCreatedSlips && data.allCreatedSlips.length > 1 
            ? `${data.allCreatedSlips.length} packing slips created successfully (split by godown)!`
            : 'Packing slip created successfully!',
          confirmButtonColor: '#4F46E5',
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827'
        });
      } else {
        setFormErrors({ submit: data.error });
      }
    } catch (error) {
      setFormErrors({ submit: error.response?.data?.error || 'Failed to create packing slip' });
      console.error('Error creating packing slip:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (packingSlip, newStatus) => {
    try {
      const response = await api.patch(`/packing-slips/${packingSlip._id}/status`, {
        status: newStatus,
        updatedBy: user.user._id
      });
      const data = response.data;

      if (data.success) {
        fetchPackingSlips();
        Swal.fire({
          icon: 'success',
          title: 'Status Updated',
          text: `Packing slip ${newStatus.toLowerCase()} successfully!`,
          timer: 2000,
          showConfirmButton: false,
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update status: ' + data.error,
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827'
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle delete
  const handleDelete = async (packingSlip) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!',
      background: isDarkMode ? '#1F2937' : '#FFFFFF',
      color: isDarkMode ? '#F3F4F6' : '#111827'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/packing-slips/${packingSlip._id}`);
      const data = response.data;

      if (data.success) {
        fetchPackingSlips();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Packing slip deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete: ' + data.error,
          background: isDarkMode ? '#1F2937' : '#FFFFFF',
          color: isDarkMode ? '#F3F4F6' : '#111827'
        });
      }
    } catch (error) {
      console.error('Error deleting packing slip:', error);
    }
  };

  // Handle print
  const handlePrint = (packingSlip) => {
    const printWindow = window.open(`/packing-slip/${packingSlip._id}`, '_blank');
    if (printWindow) {
      printWindow.focus();
    }
  };

  // Get status color classes
  const getStatusClasses = (status) => {
    const classes = {
      'Draft': isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800',
      'Issued': isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800',
      'Completed': isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
      'Cancelled': isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
    };
    return classes[status] || (isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800');
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      'Draft': <FaEdit className="mr-1" />,
      'Issued': <FaShippingFast className="mr-1" />,
      'Completed': <FaCheck className="mr-1" />,
      'Cancelled': <FaTimesCircle className="mr-1" />
    };
    return icons[status] || null;
  };

  // Count slips by status
  const getStatusCounts = () => {
    const counts = { Draft: 0, Issued: 0, Completed: 0, Cancelled: 0 };
    packingSlips.forEach(slip => {
      if (counts[slip.status] !== undefined) counts[slip.status]++;
    });
    return counts;
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const statusCounts = getStatusCounts();

  return (
    <div className={`p-4 md:p-6 min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`mb-6 p-5 rounded-xl ${isDarkMode ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'} shadow-lg`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center">
              <FaBoxOpen className="mr-3" />
              Packing Slips
            </h1>
            <p className="text-blue-100 mt-1 text-sm md:text-base">Generate and manage packing slips for delivery order items</p>
          </div>
          <button
            onClick={initializeCreateForm}
            className="mt-3 md:mt-0 inline-flex items-center justify-center px-5 py-2.5 bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition-all shadow-md text-sm"
          >
            <FaPlus className="mr-2" />
            New Packing Slip
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6">
        <div className={`p-4 rounded-xl shadow-sm border ${dm.card}`}>
          <div className="flex items-center">
            <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-indigo-900' : 'bg-indigo-100'}`}>
              <FaClipboardList className={`text-lg ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${dm.textSecondary}`}>Total</p>
              <p className={`text-xl font-bold ${dm.text}`}>{totalCount}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl shadow-sm border ${dm.card}`}>
          <div className="flex items-center">
            <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <FaEdit className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${dm.textSecondary}`}>Draft</p>
              <p className={`text-xl font-bold ${dm.text}`}>{statusCounts.Draft}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl shadow-sm border ${dm.card}`}>
          <div className="flex items-center">
            <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
              <FaShippingFast className={`text-lg ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${dm.textSecondary}`}>Issued</p>
              <p className={`text-xl font-bold ${dm.text}`}>{statusCounts.Issued}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl shadow-sm border ${dm.card}`}>
          <div className="flex items-center">
            <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-green-900' : 'bg-green-100'}`}>
              <FaCheck className={`text-lg ${isDarkMode ? 'text-green-300' : 'text-green-600'}`} />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${dm.textSecondary}`}>Completed</p>
              <p className={`text-xl font-bold ${dm.text}`}>{statusCounts.Completed}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl shadow-sm border col-span-2 md:col-span-1 ${dm.card}`}>
          <div className="flex items-center">
            <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-red-900' : 'bg-red-100'}`}>
              <FaTimesCircle className={`text-lg ${isDarkMode ? 'text-red-300' : 'text-red-600'}`} />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${dm.textSecondary}`}>Cancelled</p>
              <p className={`text-xl font-bold ${dm.text}`}>{statusCounts.Cancelled}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`p-4 rounded-xl shadow-sm border mb-6 ${dm.card}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className={`block text-sm font-medium ${dm.label} mb-1`}>Search</label>
            <div className="relative">
              <FaSearch className={`absolute left-3 top-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search by slip number, DO number, contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
              />
            </div>
          </div>
          
          <div>
            <label className={`block text-sm font-medium ${dm.label} mb-1`}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
            >
              <option value="">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Issued">Issued</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={resetFilters}
              className={`flex items-center px-4 py-2 border rounded-lg transition-colors ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              <FaSync className="mr-2 text-sm" />
              Reset
            </button>
            <button
              onClick={() => fetchPackingSlips()}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${isDarkMode ? 'bg-indigo-700 text-white hover:bg-indigo-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              <FaSearch className="mr-2 text-sm" />
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Packing Slips Table */}
      <div className={`rounded-xl shadow-sm border overflow-hidden ${dm.card}`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${dm.tableDivide}`}>
            <thead className={dm.tableHeader}>
              <tr>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Packing Slip
                </th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Delivery Order
                </th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Contact Person
                </th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Vehicle
                </th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Status
                </th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Date
                </th>
                <th className={`px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${dm.tableDivide}`}>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-2"></div>
                      <span className={`text-sm ${dm.textSecondary}`}>Loading packing slips...</span>
                    </div>
                  </td>
                </tr>
              ) : packingSlips.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <FaBoxOpen className={`mx-auto text-4xl mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={`text-sm font-medium ${dm.textSecondary}`}>No packing slips found</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Create a new packing slip to get started</p>
                  </td>
                </tr>
              ) : (
                packingSlips.map((slip) => (
                  <tr key={slip._id} className={`${dm.hover} transition-colors`}>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className={`text-sm font-semibold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                        {slip.packingSlipNumber}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className={`text-sm font-medium ${dm.text}`}>
                        {slip.deliveryOrder?.doNumber || slip.deliveryOrder?.Manual_dO_Number}
                      </div>
                      {slip.deliveryOrder?.recipient && (
                        <div className={`text-xs ${dm.textSecondary}`}>
                          {slip.deliveryOrder.recipient.name}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaUser className={`mr-1.5 text-xs ${dm.textSecondary}`} />
                        <div>
                          <div className={`text-sm ${dm.text}`}>{slip.contactPersonName}</div>
                          <div className={`text-xs ${dm.textSecondary}`}>
                            <FaPhone className="inline mr-1 text-xs" />
                            {slip.contactPersonPhone}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-700'}`}>
                        <FaTruck className="mr-1.5" />
                        {slip.vehicleNumber}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-lg ${getStatusClasses(slip.status)}`}>
                        {getStatusIcon(slip.status)}
                        {slip.status}
                      </span>
                    </td>
                    <td className={`px-5 py-3.5 whitespace-nowrap text-sm ${dm.textSecondary}`}>
                      {new Date(slip.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedPackingSlip(slip);
                            setShowViewModal(true);
                          }}
                          className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        
                        <button
                          onClick={() => handlePrint(slip)}
                          className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-green-50'}`}
                          title="Print"
                        >
                          <FaPrint />
                        </button>

                        {slip.status === 'Draft' && (
                          <button
                            onClick={() => handleStatusUpdate(slip, 'Issued')}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-indigo-400 hover:bg-gray-700' : 'text-indigo-600 hover:bg-indigo-50'}`}
                            title="Mark as Issued"
                          >
                            <FaShippingFast />
                          </button>
                        )}

                        {slip.status === 'Issued' && (
                          <button
                            onClick={() => handleStatusUpdate(slip, 'Completed')}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-green-50'}`}
                            title="Mark as Completed"
                          >
                            <FaCheck />
                          </button>
                        )}

                        {slip.status === 'Draft' && (
                          <button
                            onClick={() => handleDelete(slip)}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'}`}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-5 py-3 flex items-center justify-between border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between flex-1 sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg disabled:opacity-50 ${isDarkMode ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg disabled:opacity-50 ${isDarkMode ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className={`text-sm ${dm.textSecondary}`}>
                  Page <span className={`font-medium ${dm.text}`}>{currentPage}</span> of <span className={`font-medium ${dm.text}`}>{totalPages}</span> ({totalCount} total)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                    className={`relative inline-flex items-center px-3 py-2 rounded-l-lg border text-sm font-medium disabled:opacity-50 ${isDarkMode ? 'border-gray-600 text-gray-400 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 text-gray-500 bg-white hover:bg-gray-50'}`}
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    const pageNum = Math.max(1, Math.min(totalPages, currentPage - 2 + idx));
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? (isDarkMode ? 'z-10 bg-indigo-900 border-indigo-500 text-indigo-200' : 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600')
                            : (isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-400 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50')
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className={`relative inline-flex items-center px-3 py-2 rounded-r-lg border text-sm font-medium disabled:opacity-50 ${isDarkMode ? 'border-gray-600 text-gray-400 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 text-gray-500 bg-white hover:bg-gray-50'}`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delivery Order Selection Modal */}
      {showDeliveryOrderModal && (
        <div className={`fixed inset-0 ${dm.modalOverlay} overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4`}>
          <div className={`relative mx-auto p-6 border w-full max-w-4xl shadow-2xl rounded-2xl flex flex-col max-h-[90vh] ${dm.modalBg} ${dm.border}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-xl font-bold ${dm.text} flex items-center`}>
                <FaFileAlt className={`mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                Select Delivery Order
                <span className={`ml-2 text-sm font-normal ${dm.textSecondary}`}>({doTotalCount} found)</span>
              </h3>
              <button
                onClick={() => setShowDeliveryOrderModal(false)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <FaTimes />
              </button>
            </div>
              
            {/* Search & Basic Filter */}
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <FaSearch className={`absolute left-3 top-3.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search by DO number or recipient name..."
                  value={deliveryOrderSearch}
                  onChange={(e) => {
                    setDeliveryOrderSearch(e.target.value);
                    setDoPage(1);
                  }}
                  className={`pl-10 w-full border rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
                />
              </div>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center justify-center px-4 py-3 rounded-xl border font-medium transition-all ${showAdvancedFilters ? (isDarkMode ? 'bg-indigo-900 border-indigo-500 text-indigo-200' : 'bg-indigo-50 border-indigo-500 text-indigo-700') : (isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50')}`}
              >
                <FaFilter className="mr-2" />
                {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
              </button>
              <button
                onClick={() => { setDoPage(1); loadAvailableDeliveryOrders(1); }}
                className="flex items-center justify-center px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-200"
              >
                <FaSearch className="mr-2" />
                Find
              </button>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className={`mb-6 p-5 rounded-2xl border-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-300 ${isDarkMode ? 'bg-gray-750 border-indigo-900/50' : 'bg-indigo-50/50 border-indigo-100'}`}>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${dm.label}`}>Recipient Name</label>
                  <input
                    type="text"
                    value={advancedFilters.recipientName}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, recipientName: e.target.value }))}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
                    placeholder="Filter by name"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${dm.label}`}>DO Number</label>
                  <input
                    type="text"
                    value={advancedFilters.doNumber}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, doNumber: e.target.value }))}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
                    placeholder="Filter by DO #"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${dm.label}`}>Godown</label>
                  <select
                    value={advancedFilters.godownId}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, godownId: e.target.value }))}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
                  >
                    <option value="">All Godowns</option>
                    {godowns.map(g => <option key={g._id} value={g._id}>{g.Name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${dm.label}`}>From Date</label>
                  <input
                    type="date"
                    value={advancedFilters.dateFrom}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${dm.label}`}>To Date</label>
                  <input
                    type="date"
                    value={advancedFilters.dateTo}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 ${dm.input}`}
                  />
                </div>
                
                <div className="lg:col-span-1 flex justify-end gap-2 mt-2">
                  <button
                    onClick={() => {
                      setAdvancedFilters({ recipientName: '', doNumber: '', dateFrom: '', dateTo: '', godownId: '' });
                      setDeliveryOrderSearch('');
                    }}
                    className={`px-4 py-2 text-xs font-bold rounded-lg border transition-all ${isDarkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-100'}`}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            )}

            {/* Delivery Orders List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
              {doLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                    <FaSync className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600" />
                  </div>
                  <p className={`mt-4 font-bold ${dm.textSecondary}`}>Fetching orders...</p>
                </div>
              ) : availableDeliveryOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <FaFileAlt className={`text-4xl ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`} />
                  </div>
                  <h4 className={`text-lg font-bold ${dm.text}`}>No Orders Found</h4>
                  <p className={`mt-1 max-w-xs ${dm.textSecondary}`}>No completed delivery orders match your current filter criteria.</p>
                  <button
                    onClick={() => {
                      setAdvancedFilters({ recipientName: '', doNumber: '', dateFrom: '', dateTo: '', godownId: '' });
                      setDeliveryOrderSearch('');
                      loadAvailableDeliveryOrders(1);
                    }}
                    className="mt-4 text-indigo-600 font-bold hover:underline"
                  >
                    Clear all filters
                  </button>
                </div>
              ) : (
                availableDeliveryOrders.map((order) => {
                  const availableItemCount = order.items?.filter(i => i.availableQuantity > 0).length || 0;
                  return (
                    <div
                      key={order._id}
                      onClick={() => handleDeliveryOrderSelect(order)}
                      className={`group p-4 border-2 rounded-2xl cursor-pointer transition-all hover:shadow-lg ${isDarkMode ? 'border-gray-700 bg-gray-800/50 hover:bg-indigo-900/20 hover:border-indigo-500' : 'border-gray-100 bg-white hover:bg-indigo-50/50 hover:border-indigo-300'}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                              {order.doNumber || order.Manual_dO_Number}
                            </h4>
                          </div>
                          <div className="flex flex-wrap items-center gap-y-1 gap-x-4">
                            <div className="flex items-center gap-1.5">
                              <FaUser className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                              <p className={`text-sm font-bold ${dm.text} truncate`}>
                                {order.recipient?.name || order.recipient?.businessName || 'Unknown Recipient'}
                              </p>
                            </div>
                            {order.recipient?.phone && (
                              <div className="flex items-center gap-1.5">
                                <FaPhone className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                                <p className={`text-xs font-medium ${dm.textSecondary}`}>{order.recipient.phone}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-3">
                            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                              <FaBoxOpen className="text-[10px]" />
                              {availableItemCount} available items
                            </div>
                            {order.godown && (
                              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                                <FaWarehouse className="text-[10px]" />
                                {order.godown.Name}
                              </div>
                            )}
                            <span className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {order.recipientType}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xs font-bold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                          <div className={`inline-block p-2 rounded-xl transition-all ${isDarkMode ? 'bg-gray-700 text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white' : 'bg-gray-100 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white'}`}>
                            <FaPlus />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {doTotalPages > 1 && (
              <div className={`flex items-center justify-between mt-4 pt-4 border-t ${dm.border}`}>
                <p className={`text-sm font-bold ${dm.textSecondary}`}>
                  {doPage} / {doTotalPages} <span className="font-normal opacity-70">({doTotalCount} total)</span>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setDoPage(prev => prev - 1); loadAvailableDeliveryOrders(doPage - 1); }}
                    disabled={doPage <= 1}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-xl border transition-all ${
                      doPage <= 1
                        ? (isDarkMode ? 'border-gray-700 text-gray-600 cursor-not-allowed' : 'border-gray-200 text-gray-300 cursor-not-allowed')
                        : (isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50')
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => { setDoPage(prev => prev + 1); loadAvailableDeliveryOrders(doPage + 1); }}
                    disabled={doPage >= doTotalPages}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-xl border transition-all ${
                      doPage >= doTotalPages
                        ? (isDarkMode ? 'border-gray-700 text-gray-600 cursor-not-allowed' : 'border-gray-200 text-gray-300 cursor-not-allowed')
                        : (isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50')
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Packing Slip Modal */}
      {showCreateModal && (
        <div className={`fixed inset-0 ${dm.modalOverlay} overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4`}>
          <div className={`relative mx-auto p-6 border w-full max-w-6xl shadow-2xl rounded-2xl flex flex-col max-h-[95vh] ${dm.modalBg} ${dm.border}`}>
            <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className={`text-xl font-bold ${dm.text} flex items-center`}>
                    <FaPlus className={`mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    Create Packing Slip
                  </h3>
                  {selectedDeliveryOrder && (
                    <div className="flex items-center gap-3 mt-1">
                      <p className={`text-sm ${dm.textSecondary}`}>
                        Order: <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>{selectedDeliveryOrder.doNumber || selectedDeliveryOrder.Manual_dO_Number}</span>
                      </p>
                      <div className="h-1 w-1 rounded-full bg-gray-400"></div>
                      <p className={`text-sm ${dm.textSecondary}`}>
                        Customer: <span className={`font-bold ${dm.text}`}>{selectedDeliveryOrder.recipient?.name || selectedDeliveryOrder.recipient?.businessName}</span>
                      </p>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <FaTimes />
                </button>
              </div>

              {/* Form Content - Scrollable */}
              <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 p-6 rounded-2xl border-2 ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                  {/* Vehicle Number */}
                  <div>
                    <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${dm.label}`}>
                      <FaTruck className="inline mr-1.5 text-xs opacity-70" /> Vehicle Number *
                    </label>
                    <input
                      type="text"
                      value={formData.vehicleNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                      className={`w-full border-2 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${formErrors.vehicleNumber ? 'border-red-500' : dm.border} ${dm.input}`}
                      placeholder="e.g. AS 01 AB 1234"
                    />
                    {formErrors.vehicleNumber && <p className="text-red-500 text-[10px] font-bold mt-1 uppercase tracking-tight">{formErrors.vehicleNumber}</p>}
                  </div>

                  {/* Contact Person Name */}
                  <div>
                    <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${dm.label}`}>
                      <FaUser className="inline mr-1.5 text-xs opacity-70" /> Contact Person *
                    </label>
                    <input
                      type="text"
                      value={formData.contactPersonName}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPersonName: e.target.value }))}
                      className={`w-full border-2 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${formErrors.contactPersonName ? 'border-red-500' : dm.border} ${dm.input}`}
                      placeholder="Name of recipient"
                    />
                    {formErrors.contactPersonName && <p className="text-red-500 text-[10px] font-bold mt-1 uppercase tracking-tight">{formErrors.contactPersonName}</p>}
                  </div>

                  {/* Contact Person Phone */}
                  <div>
                    <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${dm.label}`}>
                      <FaPhone className="inline mr-1.5 text-xs opacity-70" /> Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.contactPersonPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactPersonPhone: e.target.value }))}
                      className={`w-full border-2 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${formErrors.contactPersonPhone ? 'border-red-500' : dm.border} ${dm.input}`}
                      placeholder="10-digit mobile number"
                      maxLength="10"
                    />
                    {formErrors.contactPersonPhone && <p className="text-red-500 text-[10px] font-bold mt-1 uppercase tracking-tight">{formErrors.contactPersonPhone}</p>}
                  </div>

                  <div className="md:col-span-3">
                    <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${dm.label}`}>
                      Remarks
                    </label>
                    <textarea
                      value={formData.remarks}
                      onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                      className={`w-full border-2 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${dm.border} ${dm.input}`}
                      rows="2"
                      placeholder="Add any specific instructions or notes for this packing slip..."
                    />
                  </div>
                </div>

                {/* Items Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`text-base font-bold ${dm.text} flex items-center`}>
                      <FaBoxOpen className={`mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                      Items to Pack
                      <span className={`ml-3 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter ${isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                        {formData.items.filter(i => i.quantity > 0).length} selected
                      </span>
                    </h4>
                    <p className={`text-xs font-medium ${dm.textSecondary}`}>
                      Selecting different godowns will automatically split into multiple slips.
                    </p>
                  </div>
                  
                  {formData.items.length === 0 ? (
                    <div className={`text-center py-16 rounded-3xl border-2 border-dashed ${isDarkMode ? 'border-gray-700 bg-gray-800/20' : 'border-gray-200 bg-gray-50'}`}>
                      <FaBoxOpen className={`mx-auto text-5xl mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`font-bold ${dm.textSecondary}`}>No packable items found</p>
                    </div>
                  ) : (
                    <div className={`overflow-x-auto rounded-2xl border-2 ${isDarkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-white shadow-sm'}`}>
                      <table className={`min-w-full divide-y ${dm.tableDivide}`}>
                        <thead className={dm.tableHeader}>
                          <tr>
                            <th className={`px-4 py-4 text-left text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Item Details</th>
                            <th className={`px-4 py-4 text-center text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Available</th>
                            <th className={`px-4 py-4 text-center text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pack Qty</th>
                            <th className={`px-4 py-4 text-left text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dispatch Godown</th>
                            <th className={`px-4 py-4 text-left text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Packing Notes</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${dm.tableDivide}`}>
                          {formData.items.map((item, index) => {
                            const isUsedUp = !item.isAvailable;
                            return (
                              <tr key={item.itemId} className={`${isUsedUp ? 'opacity-40 grayscale pointer-events-none' : dm.hover} transition-all`}>
                                <td className="px-4 py-4">
                                  <div className={`text-sm font-bold ${dm.text}`}>{item.Name}</div>
                                  <div className={`text-[10px] font-medium ${dm.textSecondary} flex items-center gap-2 mt-1`}>
                                    <span>DO: {item.totalQuantity} {item.units}</span>
                                    <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                                    <span>Used: {item.usedQuantity}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl ${isUsedUp ? (isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700') : (isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700')}`}>
                                    {item.maxQuantity} {item.units}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max={item.maxQuantity}
                                    value={item.quantity || ''}
                                    onChange={(e) => updateItemQuantity(index, e.target.value)}
                                    className={`w-24 border-2 rounded-xl px-3 py-2 text-center text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all ${item.quantity > 0 ? (isDarkMode ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-500 bg-indigo-50/30') : dm.border} ${dm.input}`}
                                    placeholder="0"
                                  />
                                </td>
                                <td className="px-4 py-4">
                                  <select
                                    disabled={item.quantity <= 0}
                                    value={item.godownId}
                                    onChange={(e) => updateItemGodown(index, e.target.value)}
                                    className={`w-full border-2 rounded-xl px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 transition-all ${item.quantity > 0 && !item.godownId ? 'border-amber-500 animate-pulse' : dm.border} ${dm.input} ${item.quantity <= 0 ? 'opacity-50' : ''}`}
                                  >
                                    <option value="">Select Godown</option>
                                    {godowns.map((g) => <option key={g._id} value={g._id}>{g.Name}</option>)}
                                  </select>
                                </td>
                                <td className="px-4 py-4">
                                  <input
                                    type="text"
                                    disabled={item.quantity <= 0}
                                    value={item.packingNotes}
                                    onChange={(e) => updateItemPackingNotes(index, e.target.value)}
                                    className={`w-full border-2 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 transition-all ${dm.border} ${dm.input} ${item.quantity <= 0 ? 'opacity-50' : ''}`}
                                    placeholder="E.g. Wrap with bubbles"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                
                {formErrors.items && (
                  <div className={`mb-6 p-4 rounded-2xl border-2 flex items-center gap-3 ${isDarkMode ? 'bg-red-900/20 border-red-800/50 text-red-300' : 'bg-red-50 border-red-100 text-red-700'}`}>
                    <FaBan className="flex-shrink-0" />
                    <p className="text-sm font-bold uppercase tracking-tight">{formErrors.items}</p>
                  </div>
                )}
              </div>

              {formErrors.submit && (
                <div className={`mb-4 p-4 rounded-2xl border-2 bg-red-900/30 border-red-800 text-red-300`}>
                  {formErrors.submit}
                </div>
              )}

              {/* Form Actions - Sticky at bottom */}
              <div className={`mt-auto pt-6 flex flex-col md:flex-row justify-end gap-3 border-t ${dm.border}`}>
                <div className="flex-1 flex items-center gap-2">
                   {formData.items.some(i => i.quantity > 0) && (
                     <div className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'bg-gray-800 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                        {new Set(formData.items.filter(i => i.quantity > 0).map(i => i.godownId)).size} Godown(s) Selected
                     </div>
                   )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={`px-8 py-3 rounded-xl font-bold transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-10 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:translate-y-0 flex items-center justify-center shadow-lg shadow-indigo-200/50`}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-3"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaCheck className="mr-2" />
                      Finalize Packing Slip
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Packing Slip Modal */}
      {showViewModal && selectedPackingSlip && (
        <div className={`fixed inset-0 ${dm.modalOverlay} overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4`}>
          <div className={`relative mx-auto p-6 border w-full max-w-4xl shadow-2xl rounded-2xl flex flex-col max-h-[90vh] ${dm.modalBg} ${dm.border}`}>
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center">
                <div className={`p-4 rounded-2xl mr-4 ${isDarkMode ? 'bg-indigo-900/50' : 'bg-indigo-100/50'}`}>
                  <FaFileAlt className={`text-2xl ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <h3 className={`text-xl font-black tracking-tight ${dm.text}`}>{selectedPackingSlip.packingSlipNumber}</h3>
                  <span className={`inline-flex items-center px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg mt-1 ${getStatusClasses(selectedPackingSlip.status)}`}>
                    {getStatusIcon(selectedPackingSlip.status)}
                    {selectedPackingSlip.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className={`p-2 rounded-xl transition-all ${isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-black'}`}
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                {/* Info Grid */}
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 p-6 rounded-2xl border-2 ${isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                  <div className="space-y-1">
                    <label className={`block text-[10px] font-bold uppercase tracking-widest ${dm.textSecondary}`}>Vehicle Details</label>
                    <p className={`text-base font-bold ${dm.text} flex items-center`}>
                      <FaTruck className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      {selectedPackingSlip.vehicleNumber}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className={`block text-[10px] font-bold uppercase tracking-widest ${dm.textSecondary}`}>Contact Person</label>
                    <div className="flex flex-col">
                        <p className={`text-base font-bold ${dm.text} flex items-center`}>
                        <FaUser className={`mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        {selectedPackingSlip.contactPersonName}
                        </p>
                        <p className={`text-xs font-bold ${dm.textSecondary} flex items-center mt-1`}>
                        <FaPhone className="mr-2 opacity-70" />
                        {selectedPackingSlip.contactPersonPhone}
                        </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className={`block text-[10px] font-bold uppercase tracking-widest ${dm.textSecondary}`}>Source Godown</label>
                    <p className={`text-base font-bold ${dm.text} flex items-center`}>
                      <FaWarehouse className={`mr-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      {selectedPackingSlip.godown?.Name || 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className={`block text-[10px] font-bold uppercase tracking-widest ${dm.textSecondary}`}>Delivery Order</label>
                    <p className={`text-base font-bold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                      {selectedPackingSlip.deliveryOrder?.doNumber || selectedPackingSlip.deliveryOrder?.Manual_dO_Number || 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className={`block text-[10px] font-bold uppercase tracking-widest ${dm.textSecondary}`}>Recipient</label>
                    <p className={`text-base font-bold ${dm.text}`}>
                      {selectedPackingSlip.deliveryOrder?.recipient?.name || 'N/A'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className={`block text-[10px] font-bold uppercase tracking-widest ${dm.textSecondary}`}>Created On</label>
                    <p className={`text-base font-bold ${dm.text} flex items-center`}>
                       <FaCalendarAlt className="mr-2 opacity-70 text-xs" />
                       {new Date(selectedPackingSlip.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                {selectedPackingSlip.remarks && (
                  <div className={`mb-6 p-4 rounded-2xl border-2 ${isDarkMode ? 'bg-amber-900/10 border-amber-900/30' : 'bg-amber-50 border-amber-100'}`}>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-1`}>Admin Remarks</label>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}>{selectedPackingSlip.remarks}</p>
                  </div>
                )}

                {/* Items Table */}
                <div className="mb-6">
                  <h4 className={`text-base font-bold ${dm.text} mb-4 flex items-center`}>
                    <FaBoxOpen className={`mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    Packed Inventory ({selectedPackingSlip.items?.length || 0} items)
                  </h4>
                  <div className={`overflow-x-auto rounded-2xl border-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-100 shadow-sm'}`}>
                    <table className={`min-w-full divide-y ${dm.tableDivide}`}>
                      <thead className={dm.tableHeader}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>#</th>
                          <th className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Item Name</th>
                          <th className={`px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Qty</th>
                          <th className={`px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Units</th>
                          <th className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Notes</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${dm.tableDivide}`}>
                        {selectedPackingSlip.items?.map((item, index) => (
                          <tr key={index} className={dm.hover}>
                            <td className={`px-4 py-4 text-sm font-bold ${dm.textSecondary}`}>{index + 1}</td>
                            <td className={`px-4 py-4 text-sm font-bold ${dm.text}`}>{item.Name}</td>
                            <td className="px-4 py-4 text-center">
                              <span className={`inline-flex px-3 py-1 text-sm font-black rounded-xl ${isDarkMode ? 'bg-green-900/40 text-green-200' : 'bg-green-100 text-green-800'}`}>
                                {item.quantity}
                              </span>
                            </td>
                            <td className={`px-4 py-4 text-center text-xs font-bold uppercase tracking-widest ${dm.textSecondary}`}>{item.units}</td>
                            <td className={`px-4 py-4 text-sm font-medium ${dm.textSecondary}`}>{item.packingNotes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
            </div>

            {/* Modal Actions */}
            <div className={`mt-6 pt-6 flex flex-col md:flex-row justify-end gap-3 border-t ${dm.border}`}>
              {selectedPackingSlip.status === 'Draft' && (
                <button
                  onClick={() => {
                    handleStatusUpdate(selectedPackingSlip, 'Issued');
                    setShowViewModal(false);
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all font-bold flex items-center justify-center text-sm shadow-md shadow-blue-200"
                >
                  <FaShippingFast className="mr-2" />
                  Issue Slip
                </button>
              )}
              {selectedPackingSlip.status === 'Issued' && (
                <button
                  onClick={() => {
                    handleStatusUpdate(selectedPackingSlip, 'Completed');
                    setShowViewModal(false);
                  }}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 hover:shadow-lg transition-all font-bold flex items-center justify-center text-sm shadow-md shadow-green-200"
                >
                  <FaCheck className="mr-2" />
                  Mark Delivered
                </button>
              )}
              <button
                onClick={() => handlePrint(selectedPackingSlip)}
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 hover:shadow-lg transition-all font-bold flex items-center justify-center text-sm shadow-md shadow-emerald-200"
              >
                <FaPrint className="mr-2" />
                Print PDF
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className={`px-8 py-3 rounded-xl font-bold transition-all text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global CSS for scrollbar */}
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${isDarkMode ? '#4b5563' : '#d1d5db'};
          border-radius: 20px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${isDarkMode ? '#6b7280' : '#9ca3af'};
        }
      `}} />
    </div>
  );
};

export default PackingSlip;
